import type { ComponentType } from 'react'

import {
  createConnectionFormRegistry,
  createResourceEntryRegistry,
  createSourceProductRegistry,
  type ConnectionFormExtension,
  type ResourceEntriesExtension,
  type SourceProductDescriptor,
} from '@krp/platform-ui'
import { AQSI_WINDOWS, aqsiConnectionForm, aqsiProductExtensionSource, aqsiResourceEntries } from '@krp/resource-aqsi-ui'
import { OWN_WINDOWS, ownConnectionForm, ownProductExtensionSource, ownResourceEntries } from '@krp/resource-own-ui'
import { ONEC_WINDOWS, onecConnectionForm, onecProductExtensionSource, onecProductPhotoUrl, onecResourceEntries } from '@krp/resource-onec-ui'

import { AqsiDiagnosticsBinding } from '../bindings/aqsi/AqsiDiagnosticsBinding'
import { OneCDiagnosticsBinding } from '../bindings/onec/OneCDiagnosticsBinding'

/**
 * Формы подключения native-пакетов для общего реестра (RP-UA1a): каждый вид —
 * одна строка. Platform вида ресурса не знает: форму, подпись секрета и
 * проверку черновика даёт пакет вида, а тип `ConnectionFormExtension`
 * проверяет совместимость его объекта здесь, при сборке.
 *
 * Порядок строк — порядок источников во всех списках (решение владельца 03.11.2026): кабинеты AQSI, затем 1С, затем
 * «Собственная»; внутри вида — по названию (`CONNECTION_FORMS.sortSources`).
 */
const FORMS: readonly ConnectionFormExtension[] = [aqsiConnectionForm, onecConnectionForm, ownConnectionForm]

export const CONNECTION_FORMS = createConnectionFormRegistry(FORMS)

/** Вид источника для пакетов, которые реестра форм не знают (выбор источника списком): метка, цвет и общий порядок. */
export const SOURCE_KINDS = {
  markOf: CONNECTION_FORMS.shortTitleOf,
  toneOf: CONNECTION_FORMS.toneOf,
  compareSources: CONNECTION_FORMS.compareSources,
} as const

/** Панель диагностики службы ресурса (`diagnostics.read`) по виду: binding native-пакета. */
export const RESOURCE_DIAGNOSTICS: Readonly<Record<string, ComponentType<{ resourceId: string }>>> = {
  [onecConnectionForm.kind]: OneCDiagnosticsBinding,
  [aqsiConnectionForm.kind]: AqsiDiagnosticsBinding,
}

/**
 * Фото товара из источника по виду ресурса (поиск по фото показывает найденное картинкой): адрес даёт пакет вида. Вида
 * здесь нет — у его карточек фото не показывается (AQSI фото не хранит).
 */
export const PRODUCT_PHOTO_URLS: Readonly<Record<string, (resourceId: string, remoteKey: string) => string>> = {
  [onecConnectionForm.kind]: onecProductPhotoUrl,
}

/**
 * Описания видов источника для блока «Источники» общей карточки товара (ADR-21, RP-FB71): как читать расширение карточки
 * даёт пакет вида, окно карточки источника — composition (окна лежат в пакетах видов, общий пакет их не знает). Вид без строки
 * здесь общая карточка показывает честным «Данные источника здесь показать нельзя».
 */
const SOURCE_PRODUCT_DESCRIPTORS: readonly SourceProductDescriptor[] = [
  {
    ...onecProductExtensionSource,
    openCard: (resourceId, remoteKey) => ({ kind: ONEC_WINDOWS.product, params: { rid: resourceId, key: remoteKey } }),
  },
  {
    ...aqsiProductExtensionSource,
    openCard: (resourceId, remoteKey) => ({ kind: AQSI_WINDOWS.good, params: { rid: resourceId, key: remoteKey } }),
  },
  {
    ...ownProductExtensionSource,
    // RP-OWN3: ключ товара своей базы в связи — его id в службе.
    openCard: (resourceId, remoteKey) => ({ kind: OWN_WINDOWS.product, params: { rid: resourceId, id: remoteKey } }),
  },
]

export const SOURCE_PRODUCTS = createSourceProductRegistry(SOURCE_PRODUCT_DESCRIPTORS)

/**
 * Входы в окна пакета вида из карточки ресурса (UA1c п. 7): список объявляет
 * пакет вида, карточка показывает кнопки и открывает окно по имени с rid
 * ресурса. Каждый вид — одна строка; что окно есть в реестре и оно ресурсное,
 * проверяет тест composition.
 */
const ENTRIES: readonly ResourceEntriesExtension[] = [onecResourceEntries, aqsiResourceEntries, ownResourceEntries]

export const RESOURCE_ENTRIES = createResourceEntryRegistry(ENTRIES)
